import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { nombreCompleto } from "../adapters/matriculasAdapter";
import { estadoMatriculaLabel } from "../constants/estadosMatricula";
import { estadoPagoLabel } from "../constants/estadosPago";

export default function MatriculasTable({
  matriculas,
  loading,
  reload,
  error,
  seleccionadoId,
  onSelect,
  onEdit,
  onDelete,
  onTransferir,
  onGenerateContract,
  onDownloadContract,
  contractLoadingId,
  contractLoadingType,
  maxHeight = "60vh",
  canEdit = true,
  canDelete = true,
  canTransfer = false,
  canGenerateContract = false,
  canDownloadContract = false,
}) {
  const navigate = useNavigate();
  const minRows = 10;
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="management-card management-alert">{error}</div>;
  }

  if (!matriculas.length) {
    return (
      <div className="management-card matriculas-empty text-center py-5">
        <i className="bi bi-journal-text matriculas-empty-icon d-block mb-3" />
        <h5 className="fw-semibold mb-1">No hay matrículas registradas.</h5>
        <p className="text-muted mb-0">Crea una matrícula o ajusta los filtros.</p>
      </div>
    );
  }

  const emptyRows = Math.max(0, minRows - matriculas.length);
  const hasActions = canEdit || canDelete || canGenerateContract || canDownloadContract || canTransfer;

  return (
    <div className="management-card matriculas-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <p className="management-label mb-1">Listado</p>
          <h6 className="mb-0">Matrículas registradas</h6>
        </div>
        {reload && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload} disabled={loading}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>
      <div className="management-table-wrapper" style={{ maxHeight }}>
        <table className="table align-middle management-table matriculas-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th>#</th>
              <th>Codigo</th>
              <th>Estudiante</th>
              <th>Promoción / Cursos</th>
              <th className="text-end">Total</th>
              <th>Estado</th>
              {hasActions && <th className="text-end">Acciones</th>}
            </tr>
          </thead>
          <tbody ref={wrapRef}>
            {matriculas.map((mat, idx) => {
              const isSelected = seleccionadoId === mat.id;
              const isContractLoading = contractLoadingId === mat.id;
              const isGenerating = isContractLoading && contractLoadingType === "generar";
              const isDownloading = isContractLoading && contractLoadingType === "descargar";
              const disableGenerate = mat.estadoPago === "pendiente";
              const isMenuOpen = openMenuId === mat.id;

              return (
                <tr
                  key={`mat-${mat.id}-${idx}`}
                  className={isSelected ? "selected-row" : ""}
                  onClick={() => onSelect?.(mat)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect?.(mat);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td className="fw-semibold">{idx + 1}</td>
                  <td className="fw-semibold">{mat.codigoMatricula || "-"}</td>
                  <td
                    onClick={(e) => { e.stopPropagation(); if (mat.estudianteId) navigate(`/dashboard/estudiantes/${mat.estudianteId}/detalle`); }}
                    style={{ cursor: mat.estudianteId ? "pointer" : "default" }}
                    title={mat.estudianteId ? "Ver detalle del estudiante" : undefined}
                  >
                    <div className="fw-semibold">{nombreCompleto(mat)}</div>
                  </td>
                  <td
                    onClick={(e) => { e.stopPropagation(); if (mat.promocionId) navigate(`/dashboard/coordinacion/promociones/${mat.promocionId}/inscritos`); }}
                    style={{ cursor: mat.promocionId ? "pointer" : "default" }}
                    title={mat.promocionId ? "Ver inscritos de la promoción" : undefined}
                  >
                    <span className="fw-semibold">{mat.promocion || "Sin promoción"}</span>
                    {mat.curso && <span className="text-muted"> / {mat.curso}</span>}
                  </td>
                  <td className="text-end fw-semibold">{mat.totalAPagar != null ? `$${Number(mat.totalAPagar).toFixed(2)}` : "—"}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <span className="badge rounded-pill bg-light text-dark border fw-semibold">
                        {estadoMatriculaLabel(mat.estadoMatricula)}
                      </span>
                      <span
                        className={`badge rounded-pill px-3 py-2 ${
                          mat.estadoPago === "pagado"
                            ? "bg-success-subtle text-success"
                            : mat.estadoPago === "parcial"
                              ? "bg-warning-subtle text-warning"
                              : "bg-secondary-subtle text-muted"
                        }`}
                        style={(mat.estadoPago === "pendiente" || mat.estadoPago === "parcial") ? { cursor: "pointer" } : undefined}
                        title={(mat.estadoPago === "pendiente" || mat.estadoPago === "parcial") ? "Registrar pago" : undefined}
                        onClick={(mat.estadoPago === "pendiente" || mat.estadoPago === "parcial") ? (e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/caja/pagos?matriculaId=${mat.id}&estudianteId=${mat.estudianteId}`);
                        } : undefined}
                      >
                        {estadoPagoLabel(mat.estadoPago)}
                      </span>
                    </div>
                  </td>
                  {hasActions && (
                    <td className="text-end">
                      <div className="management-actions-wrap">
                        <button
                          type="button"
                          className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isMenuOpen) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setOpenMenuId(isMenuOpen ? null : mat.id);
                          }}
                          title="Acciones"
                          aria-expanded={isMenuOpen}
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>
                        {isMenuOpen && (
                          <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                            {canGenerateContract && onGenerateContract && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onGenerateContract(mat); }}
                                  disabled={disableGenerate || isContractLoading}
                                  title={disableGenerate ? "Registra al menos un pago para habilitar la generación del contrato." : undefined}
                                >
                                  {isGenerating
                                    ? <span className="spinner-border spinner-border-sm" role="status" />
                                    : <i className="bi bi-file-earmark-plus" />}
                                  Generar contrato
                                </button>
                              </li>
                            )}
                            {canDownloadContract && onDownloadContract && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDownloadContract(mat); }}
                                  disabled={isContractLoading}
                                >
                                  {isDownloading
                                    ? <span className="spinner-border spinner-border-sm" role="status" />
                                    : <i className="bi bi-download" />}
                                  Descargar contrato
                                </button>
                              </li>
                            )}
                            {canTransfer && onTransferir && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onTransferir(mat); }}
                                  disabled={!['activa','reservada','suspendida'].includes(mat.estadoMatricula)}
                                  title={!['activa','reservada','suspendida'].includes(mat.estadoMatricula) ? 'Solo se puede transferir matrículas activas, reservadas o suspendidas' : undefined}
                                >
                                  <i className="bi bi-arrow-left-right" />
                                  Transferir
                                </button>
                              </li>
                            )}
                            {(canGenerateContract || canDownloadContract || canTransfer) && (canEdit || canDelete) && (
                              <li className="menu-divider" />
                            )}
                            {canEdit && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit?.(mat); }}
                                >
                                  <i className="bi bi-pencil-square" />
                                  Editar
                                </button>
                              </li>
                            )}
                            {canDelete && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item is-danger"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDelete?.(mat); }}
                                >
                                  <i className="bi bi-trash" />
                                  Eliminar
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array.from({ length: emptyRows }).map((_, idx) => (
                <tr key={`mat-empty-${idx}`} className="table-empty-row" aria-hidden="true">
                  <td colSpan={hasActions ? 7 : 6}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
